import {useState} from 'react';

import useOnMount from 'ui-library/hooks/useOnMount';

/**
 * Custom React hook to delay rendering components.
 *
 * The caller should use the output (`shouldRender`) to short-circuit rendering if it is true.
 * Passing in initialShouldRender = true will lead to shouldRender always being true - this is for
 * convenience as you cannot conditionally call hooks.
 *
 * Example:
 * function Component() {
 *   const shouldRender = useDelayedRender();
 *   if (!shouldRender) {
 *     return <LoadingPaneContents />;
 *   }
 *
 *   // expensive computations
 *   return <ExpensiveToRenderComponent />;
 * }
 */
export default function useDelayedRender(initialShouldRender?: boolean): boolean {
  const [shouldRender, setShouldRender] = useState(initialShouldRender ?? false);

  useOnMount(() => {
    setTimeout(() => {
      setShouldRender(true);
    }, 0);
  });

  return shouldRender;
}
